import { linter, Diagnostic, lintGutter } from "@codemirror/lint";
import { EditorView } from "@codemirror/view";
import { formatCSS, formatHTML } from "./code-formatter";

/**
 * Extracts line and column information from error messages
 */
function extractLineInfo(
  message: string
): { line: number; column: number } | null {
  const lineMatch = message.match(/(\d+):(\d+)/);
  if (lineMatch) {
    return { line: parseInt(lineMatch[1]), column: parseInt(lineMatch[2]) };
  }
  return null;
}

/**
 * Formats error messages by removing error type prefixes
 */
function formatMessage(message: string): string {
  return message
    .replace("CssSyntaxError: ", "")
    .replace("HtmlSyntaxError: ", "");
}

/**
 * CSS linter that uses Prettier's formatCSS function to detect syntax errors
 * Replaces %% variables with __ to avoid parsing errors during linting
 */
async function cssTreeLinter(view: EditorView): Promise<Diagnostic[]> {
  const diagnostics: Diagnostic[] = [];

  // Replace special variables with a placeholder to avoid parsing errors
  const code = view.state.doc.toString().replaceAll("%%", "__");

  try {
    await formatCSS(code);
  } catch (error: any) {
    const lineInfo = extractLineInfo(error.message);
    if (lineInfo) {
      const from =
        view.state.doc.line(lineInfo.line).from + lineInfo.column - 1;
      const to = from + 1;
      diagnostics.push({
        from,
        to,
        severity: "error",
        message: formatMessage(error.message)
      });
    }
  }

  return diagnostics;
}

/**
 * HTML linter that uses Prettier's formatHTML function to detect syntax errors
 */
async function htmlTreeLinter(view: EditorView): Promise<Diagnostic[]> {
  const diagnostics: Diagnostic[] = [];

  const code = view.state.doc.toString();

  try {
    await formatHTML(code);
  } catch (error: any) {
    const lineInfo = extractLineInfo(error.message);
    if (lineInfo) {
      const from =
        view.state.doc.line(lineInfo.line).from + lineInfo.column - 1;
      const to = from + 1;
      diagnostics.push({
        from,
        to,
        severity: "error",
        message: formatMessage(error.message)
      });
    }
  }

  return diagnostics;
}

// Export lint extensions for both CSS and HTML
export const cssLintExtension = [linter(cssTreeLinter), lintGutter()];
export const htmlLintExtension = [linter(htmlTreeLinter), lintGutter()];
